import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  CheckCircleIcon,
  XCircleIcon,
  ArrowsClockwiseIcon,
  PaperPlaneTiltIcon,
  WarningIcon,
  PlugsConnectedIcon,
  CopyIcon,
  ShieldIcon,
} from "@phosphor-icons/react";

function ProviderCard({ p, onUpdate, onDelete }) {
  const [edit, setEdit] = useState({
    name: p.name,
    base_url: p.base_url,
    api_key: p.api_key,
    default_model: p.default_model,
    enabled: p.enabled,
  });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [models, setModels] = useState([]);

  const save = async () => {
    await api.patch(`/providers/${p.id}`, edit);
    toast.success(`${edit.name} saved`);
    onUpdate();
  };

  const test = async () => {
    setTesting(true);
    try {
      await api.patch(`/providers/${p.id}`, edit); // save before test
      const { data } = await api.post(`/providers/${p.id}/test`);
      setTestResult(data);
      setModels(data.models || []);
      if (data.ok) toast.success(`Connected. ${data.models_count} models found.`);
      else toast.error(`Connection failed: ${data.error}`);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div
      className="border border-zinc-800 bg-zinc-900 rounded-sm p-5 flex flex-col gap-3"
      data-testid={`provider-card-${p.type}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="font-overline text-[10px] text-zinc-500">
            {p.type.replace("_", " ")}
          </div>
          <Input
            value={edit.name}
            onChange={(e) => setEdit({ ...edit, name: e.target.value })}
            className="mt-1 bg-transparent border-0 px-0 text-base font-medium tracking-tight focus-visible:ring-0"
          />
        </div>
        <div className="flex items-center gap-2">
          {testResult ? (
            testResult.ok ? (
              <Badge className="rounded-sm bg-emerald-950/40 text-emerald-300 border border-emerald-800 font-mono text-[10px]">
                <CheckCircleIcon size={10} className="mr-1" /> connected
              </Badge>
            ) : (
              <Badge className="rounded-sm bg-red-950/40 text-red-300 border border-red-800 font-mono text-[10px]">
                <XCircleIcon size={10} className="mr-1" /> failed
              </Badge>
            )
          ) : null}
          <Switch
            checked={edit.enabled}
            onCheckedChange={(v) => setEdit({ ...edit, enabled: v })}
            data-testid={`provider-enabled-${p.type}`}
          />
        </div>
      </div>

      <div className="grid gap-3">
        <div>
          <Label className="font-overline text-[10px] text-zinc-500">
            Base URL
          </Label>
          <Input
            value={edit.base_url}
            onChange={(e) => setEdit({ ...edit, base_url: e.target.value })}
            className="mt-1 rounded-sm bg-black border-zinc-800 font-mono text-xs"
          />
        </div>
        <div>
          <Label className="font-overline text-[10px] text-zinc-500">
            API Key
          </Label>
          <Input
            value={edit.api_key}
            onChange={(e) => setEdit({ ...edit, api_key: e.target.value })}
            type="password"
            placeholder={p.type === "openrouter" ? "sk-or-v1-..." : "(optional for local)"}
            className="mt-1 rounded-sm bg-black border-zinc-800 font-mono text-xs"
          />
        </div>
        <div>
          <Label className="font-overline text-[10px] text-zinc-500">
            Default Model
          </Label>
          <Input
            value={edit.default_model}
            onChange={(e) =>
              setEdit({ ...edit, default_model: e.target.value })
            }
            placeholder="e.g. llama3.2, openai/gpt-4o-mini"
            className="mt-1 rounded-sm bg-black border-zinc-800 font-mono text-xs"
          />
        </div>
      </div>

      {models.length > 0 && (
        <div className="border border-zinc-800 bg-black rounded-sm p-2 max-h-32 overflow-y-auto">
          <div className="font-overline text-[10px] text-zinc-500 mb-1">
            Discovered Models
          </div>
          <div className="flex flex-wrap gap-1">
            {models.slice(0, 30).map((m) => (
              <button
                key={m.id}
                onClick={() => setEdit({ ...edit, default_model: m.id })}
                className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm border border-zinc-800 hover:border-zinc-600 text-zinc-300 hover:text-white"
              >
                {m.id}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <Button
          onClick={test}
          disabled={testing}
          data-testid={`provider-test-${p.type}`}
          className="rounded-sm bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-xs h-8"
        >
          <ArrowsClockwiseIcon
            size={12}
            className={`mr-1.5 ${testing ? "animate-spin" : ""}`}
          />
          {testing ? "Testing..." : "Test & List"}
        </Button>
        <Button
          onClick={save}
          data-testid={`provider-save-${p.type}`}
          className="rounded-sm bg-white text-black hover:bg-zinc-200 text-xs h-8"
        >
          Save
        </Button>
      </div>
    </div>
  );
}

export default function Settings() {
  const [providers, setProviders] = useState([]);
  const [settings, setSettings] = useState(null);
  const [tgStatus, setTgStatus] = useState(null);
  const [authChatIdInput, setAuthChatIdInput] = useState("");

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    const [pr, st, tg] = await Promise.all([
      api.get("/providers"),
      api.get("/settings"),
      api.get("/telegram/status"),
    ]);
    setProviders(pr.data);
    setSettings(st.data);
    setTgStatus(tg.data);
  };

  const update = async (patch) => {
    const { data } = await api.patch("/settings", patch);
    setSettings(data);
    // refresh tg status
    const tg = await api.get("/telegram/status");
    setTgStatus(tg.data);
  };

  if (!settings)
    return (
      <div className="p-8 text-zinc-500 font-mono text-xs">Loading...</div>
    );

  const activeProvider = providers.find(
    (p) => p.id === settings.active_provider_id,
  );

  return (
    <div className="h-full overflow-y-auto" data-testid="settings-page">
      <div className="max-w-6xl mx-auto p-8 fade-up">
        <div className="mb-8">
          <div className="font-overline text-[10px] text-zinc-500">
            Configuration
          </div>
          <h1 className="font-heading text-4xl tracking-tighter font-medium">
            Settings
          </h1>
        </div>

        {/* Active runtime */}
        <section className="mb-10">
          <div className="font-overline text-[10px] text-zinc-500 mb-3">
            Active Runtime
          </div>
          <div className="border border-zinc-800 bg-zinc-900 rounded-sm p-5 grid md:grid-cols-2 gap-4">
            <div>
              <Label className="font-overline text-[10px] text-zinc-500">
                Provider
              </Label>
              <Select
                value={settings.active_provider_id || ""}
                onValueChange={(v) => update({ active_provider_id: v })}
              >
                <SelectTrigger
                  data-testid="active-provider-select"
                  className="mt-1 rounded-sm bg-black border-zinc-800"
                >
                  <SelectValue placeholder="Select provider..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {providers
                    .filter((p) => p.enabled)
                    .map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-overline text-[10px] text-zinc-500">
                Model
              </Label>
              <Input
                data-testid="active-model-input"
                value={settings.active_model || ""}
                onChange={(e) =>
                  setSettings({ ...settings, active_model: e.target.value })
                }
                onBlur={() => update({ active_model: settings.active_model })}
                placeholder={
                  activeProvider?.default_model ||
                  "Type model id (e.g. llama3.2)"
                }
                className="mt-1 rounded-sm bg-black border-zinc-800 font-mono text-xs"
              />
            </div>
          </div>
        </section>

        {/* Providers */}
        <section className="mb-10">
          <div className="font-overline text-[10px] text-zinc-500 mb-3">
            LLM Providers
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {providers.map((p) => (
              <ProviderCard key={p.id} p={p} onUpdate={load} />
            ))}
          </div>
        </section>

        {/* Agent behavior */}
        <section className="mb-10">
          <div className="font-overline text-[10px] text-zinc-500 mb-3">
            Agent Behavior
          </div>
          <div className="border border-zinc-800 bg-zinc-900 rounded-sm p-5 space-y-5">
            <div>
              <Label className="font-overline text-[10px] text-zinc-500">
                System Prompt
              </Label>
              <Textarea
                data-testid="system-prompt"
                value={settings.system_prompt}
                onChange={(e) =>
                  setSettings({ ...settings, system_prompt: e.target.value })
                }
                onBlur={() =>
                  update({ system_prompt: settings.system_prompt })
                }
                rows={5}
                className="mt-1 rounded-sm bg-black border-zinc-800 font-mono text-xs"
              />
            </div>

            <div>
              <Label className="font-overline text-[10px] text-zinc-500">
                Max Tool Iterations: {settings.max_iterations}
              </Label>
              <Slider
                min={1}
                max={12}
                step={1}
                value={[settings.max_iterations]}
                onValueChange={([v]) =>
                  setSettings({ ...settings, max_iterations: v })
                }
                onValueCommit={([v]) => update({ max_iterations: v })}
                className="mt-3"
              />
            </div>

            <div className="flex items-center justify-between border-t border-zinc-800 pt-4">
              <div>
                <div className="text-sm font-medium">
                  Sandboxed Execution
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  Restrict code & shell to{" "}
                  <code className="font-mono text-yellow-400">
                    /app/agent_workspace
                  </code>
                  . Disable to give full host access.
                </div>
              </div>
              <Switch
                data-testid="sandbox-toggle"
                checked={settings.sandboxed_execution}
                onCheckedChange={(v) => update({ sandboxed_execution: v })}
              />
            </div>

            {!settings.sandboxed_execution && (
              <div className="flex items-start gap-2 text-xs text-red-300 bg-red-950/30 border border-red-900 rounded-sm p-3">
                <WarningIcon size={16} className="shrink-0 mt-0.5" />
                <span>
                  Unsandboxed mode is ON. The agent can read/write any file on
                  the host and run arbitrary shell commands.
                </span>
              </div>
            )}

            <div className="border-t border-zinc-800 pt-4">
              <div className="font-overline text-[10px] text-zinc-500 mb-2">
                Tool Toggles
              </div>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(settings.tools_enabled || {}).map(([k, v]) => (
                  <label
                    key={k}
                    className="flex items-center justify-between border border-zinc-800 bg-black rounded-sm px-3 py-2 cursor-pointer hover:border-zinc-700"
                  >
                    <span className="font-mono text-xs">{k}</span>
                    <Switch
                      data-testid={`tool-toggle-${k}`}
                      checked={!!v}
                      onCheckedChange={(checked) =>
                        update({
                          tools_enabled: {
                            ...settings.tools_enabled,
                            [k]: checked,
                          },
                        })
                      }
                    />
                  </label>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Telegram */}
        <section className="mb-10">
          <div className="font-overline text-[10px] text-zinc-500 mb-3 flex items-center gap-2">
            <PaperPlaneTiltIcon size={12} /> Telegram Bot
          </div>
          <div className="border border-zinc-800 bg-zinc-900 rounded-sm p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Enable polling</div>
                <div className="text-xs text-zinc-500 mt-0.5 font-mono">
                  Token configured:{" "}
                  {tgStatus?.token_configured ? "yes" : "no"} · Running:{" "}
                  {tgStatus?.running ? "yes" : "no"}
                </div>
              </div>
              <Switch
                data-testid="telegram-toggle"
                checked={settings.telegram_enabled}
                onCheckedChange={(v) => update({ telegram_enabled: v })}
              />
            </div>

            <div>
              <Label className="font-overline text-[10px] text-zinc-500">
                Authorized Chat IDs
              </Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {(settings.telegram_authorized_chat_ids || []).length === 0 && (
                  <span className="text-xs text-zinc-600 font-mono">
                    none (any chat ID accepted - locks to first user)
                  </span>
                )}
                {settings.telegram_authorized_chat_ids?.map((cid) => (
                  <Badge
                    key={cid}
                    className="rounded-sm bg-zinc-800 border border-zinc-700 text-zinc-200 font-mono"
                  >
                    {cid}
                    <button
                      onClick={() =>
                        update({
                          telegram_authorized_chat_ids:
                            settings.telegram_authorized_chat_ids.filter(
                              (x) => x !== cid,
                            ),
                        })
                      }
                      className="ml-2 text-zinc-500 hover:text-red-400"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <Input
                  value={authChatIdInput}
                  onChange={(e) => setAuthChatIdInput(e.target.value)}
                  placeholder="Send /start to your bot to get your chat ID"
                  className="rounded-sm bg-black border-zinc-800 font-mono text-xs"
                />
                <Button
                  data-testid="add-chat-id-button"
                  onClick={() => {
                    const n = parseInt(authChatIdInput, 10);
                    if (!Number.isFinite(n)) return;
                    update({
                      telegram_authorized_chat_ids: [
                        ...(settings.telegram_authorized_chat_ids || []),
                        n,
                      ],
                    });
                    setAuthChatIdInput("");
                  }}
                  className="rounded-sm bg-zinc-800 border border-zinc-700 hover:bg-zinc-700 text-xs"
                >
                  Add
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Webhook channel */}
        <section className="mb-10">
          <div className="font-overline text-[10px] text-zinc-500 mb-3 flex items-center gap-2">
            <PlugsConnectedIcon size={12} /> Webhook Channel
          </div>
          <div className="border border-zinc-800 bg-zinc-900 rounded-sm p-5 space-y-5">
            <div>
              <Label className="font-overline text-[10px] text-zinc-500 flex items-center gap-2">
                <ShieldIcon size={10} /> Inbound URL (POST text to invoke the agent)
              </Label>
              <div className="mt-2 flex gap-2">
                <Input
                  data-testid="webhook-inbound-url"
                  readOnly
                  value={`${window.location.origin}/api/webhook/${settings.webhook_inbound_secret}`}
                  className="rounded-sm bg-black border-zinc-800 font-mono text-xs"
                />
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `${window.location.origin}/api/webhook/${settings.webhook_inbound_secret}`,
                    );
                    toast.success("Copied");
                  }}
                  className="rounded-sm bg-zinc-800 border border-zinc-700 hover:bg-zinc-700"
                >
                  <CopyIcon size={14} />
                </Button>
                <Button
                  data-testid="webhook-regenerate"
                  onClick={async () => {
                    if (!confirm("Regenerate secret? Existing integrations will break.")) return;
                    const { data } = await api.post("/webhook/regenerate_secret");
                    setSettings({ ...settings, webhook_inbound_secret: data.webhook_inbound_secret });
                    toast.success("Secret regenerated");
                  }}
                  className="rounded-sm bg-zinc-800 border border-zinc-700 hover:bg-zinc-700"
                >
                  <ArrowsClockwiseIcon size={14} />
                </Button>
              </div>
              <div className="mt-2 text-[10px] font-mono text-zinc-600 leading-relaxed">
                POST JSON: <span className="text-zinc-300">{`{"text": "...", "conversation_id"?: "...", "collection_ids"?: [...]}`}</span>
              </div>
            </div>

            <div className="border-t border-zinc-800 pt-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-sm font-medium">Outbound notifications</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    DoppelGanger POSTs each agent reply to your URL (Zapier, n8n, Slack incoming webhook, Discord webhook...).
                  </div>
                </div>
                <Switch
                  data-testid="webhook-outbound-toggle"
                  checked={settings.webhook_outbound_enabled}
                  onCheckedChange={(v) => update({ webhook_outbound_enabled: v })}
                />
              </div>
              <div className="flex gap-2 mt-2">
                <Input
                  data-testid="webhook-outbound-url"
                  value={settings.webhook_outbound_url}
                  onChange={(e) =>
                    setSettings({ ...settings, webhook_outbound_url: e.target.value })
                  }
                  onBlur={() =>
                    update({ webhook_outbound_url: settings.webhook_outbound_url })
                  }
                  placeholder="https://hooks.your-service/..."
                  className="rounded-sm bg-black border-zinc-800 font-mono text-xs"
                />
                <Button
                  data-testid="webhook-test-outbound"
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/webhook/test_outbound");
                      if (data.ok) toast.success(`Sent · HTTP ${data.status}`);
                      else toast.error(data.error || "Failed");
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Failed");
                    }
                  }}
                  className="rounded-sm bg-zinc-800 border border-zinc-700 hover:bg-zinc-700 text-xs"
                >
                  Test
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
